import React from "react";
import { Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { QUIZ } from "@/theme/quizTokens";

export const QUIZ_TABS = ["My dog", "Me", "My plan", "Summary"] as const;
export type QuizTabIndex = 0 | 1 | 2 | 3;

type Props = {
  /** Which of the 4 macro tabs is active (0 = My dog … 3 = Summary). */
  activeTab: QuizTabIndex;
  onClose?: () => void;
  /** Hero screens (dog count / names) have no ✕ on mobile in the Figma. */
  hideCloseOnMobile?: boolean;
};

/**
 * The 4-segment macro progress bar from the Figma redesign (My dog / Me / My plan
 * / Summary) plus a close (X) button. Replaces the old per-question pill stepper.
 * A segment is filled (blue) when its tab is active or already completed.
 */
export function QuizTabProgress({ activeTab, onClose, hideCloseOnMobile }: Props) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: 375, md: 500 },
        mx: "auto",
        px: { xs: "30px", md: 0 },
        pt: { xs: "20px", md: 0 },
      }}
    >
      {/* segments + close */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "10px", md: "12px" } }}>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: { xs: "9px", md: "12px" } }}>
          {QUIZ_TABS.map((tab, i) => (
            <Box
              key={tab}
              sx={{
                flex: 1,
                height: { xs: "3.5px", md: "5px" },
                borderRadius: "100px",
                backgroundColor: i <= activeTab ? QUIZ.color.blue : QUIZ.color.cream2,
                transition: "background-color 0.25s ease",
              }}
            />
          ))}
        </Box>
        <Box
          role="button"
          aria-label="Close quiz"
          onClick={onClose}
          sx={{
            display: { xs: hideCloseOnMobile ? "none" : "flex", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 21, md: 32 },
            height: { xs: 21, md: 32 },
            color: QUIZ.color.dark,
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": { opacity: 0.7 },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
        </Box>
      </Box>

      {/* labels */}
      <Box
        sx={{
          display: "flex",
          mt: { xs: "12px", md: "10px" },
          // labels align under the segments (close button column excluded)
          pr: { xs: "31px", md: "44px" },
        }}
      >
        {QUIZ_TABS.map((tab) => (
          <Box
            key={tab}
            sx={{
              flex: 1,
              textAlign: "center",
              fontFamily: QUIZ.font.body,
              fontSize: { xs: "12.16px", md: "15px" },
              lineHeight: 1.3,
              color: QUIZ.color.dark,
            }}
          >
            {tab}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default QuizTabProgress;
